import React, { useState } from "react";
import { hot } from "react-hot-loader";
import { defer, map, reduce } from "lodash";
import firebase from "firebase";
import {
  Breadcrumbs,
  Link,
  Typography,
  Box,
  AppBar,
  Table,
  TableBody,
  Menu,
  MenuItem,
  TextField,
  ListItemIcon,
  Grid,
  LinearProgress
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  CreateNewFolder as CreateNewFolderIcon,
  Delete as RemoveIcon
} from "@material-ui/icons";
import TableHead from "./TableHead";
import RowSkeleton from "./RowSkeleton";
import Dialog from "../ui/Dialog";
import FileUploader from "../FileUploader";
import { localForage } from "../../utils/misc";
import FolderRow from "./FolderRow";
import FileRow from "./FileRow";
import { createPlaceholderFile } from "../../utils/files";

const useStyles = makeStyles(theme => ({
  root: {
    position: "relative"
  },
  inline: {
    display: "inline-flex"
  },
  close: {
    padding: "0.5rem"
  },
  avatar: {
    textTransform: "uppercase",
    fontSize: "12px",
    fontWeight: "bold",
    display: "inline-flex",
    verticalAlign: "middle"
  },
  preview: {
    display: "inline-flex",
    verticalAlign: "middle",
    width: 48,
    height: 48,
    textAlign: "center",
    justifyContent: "center",
    overflow: "hidden",
    "& img": {
      maxHeight: "100%"
    }
  },
  tableRow: {
    cursor: "pointer",
    "& td": {
      paddingTop: "0.5rem",
      paddingBottom: "0.5rem"
    }
  },
  breadCrumb: {
    cursor: "pointer"
  },
  loader: {
    float: "left",
    marginRight: "0.5rem"
  },
  uploadBar: {
    borderLeft: `${theme.palette.grey[300]} 1px solid`
  }
}));

const FileList = props => {
  const classes = useStyles();
  const [files, setFiles] = useState({ folders: [], files: [] });
  const [hasLoaded, setHasLoaded] = useState(true);
  const [hasMounted, setHasMounted] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [path, setPath] = useState("");
  const [menuContext, setMenuContext] = useState({});
  const [newFolderName, setNewFolderName] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState();
  let columns = [
    { id: "#", label: "#", maxWidth: 24, align: "center" },
    { id: "preview", label: "Name", maxWidth: 32, align: "center" },
    { id: "name", label: "" },
    { id: "type", label: "Type" }
  ];

  if (props.selectedFiles) {
    columns = [
      ...columns.slice(0, 3),
      {
        id: "selected",
        align: "center",
        maxWidth: 48
      },
      ...columns.slice(3)
    ];
  }

  if (!hasMounted) {
    localForage.getItem("filePath", (err, val) => {
      if (val && val !== path) {
        handlePathChange(val)();
      } else {
        setHasMounted(true);
        setHasLoaded(false);
      }
    });
  }

  const getFiles = async () => {
    const serverFiles = [];
    const serverFolders = [];
    try {
      const data = await firebase
        .storage()
        .ref(path)
        .listAll();
      data.prefixes.map(function(folderRef) {
        serverFolders.push(folderRef);
      });
      data.items.map(function(itemRef) {
        // itemRef.updateMetadata({
        //   cacheControl: "public,max-age=31536000,no-cache" // one year
        // });
        serverFiles.push(itemRef);
      });

      setHasLoaded(true);
      setFiles({ folders: serverFolders, files: serverFiles });
    } catch (e) {
      console.error(e);
    }
  };

  if (!hasLoaded) {
    getFiles();
  }

  const createFolder = async () => {
    if (newFolderName) {
      try {
        setLoading(true);
        await createPlaceholderFile(`${menuContext.path}/${newFolderName}`);
        setNewFolderName("");
        if (path !== menuContext.path) {
          handlePathChange(menuContext.path)();
        }
        setHasLoaded(false);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
  };

  const deleteFile = async () => {
    if (menuContext.path && menuContext.type === "file") {
      try {
        await firebase
          .storage()
          .ref(menuContext.path)
          .delete();
        props.removeFile(menuContext.file);
        setHasLoaded(false);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const deleteFolder = async (
    contextPath,
    promises = [],
    removeSelectedFiles = []
  ) => {
    const usePath = contextPath ? contextPath : menuContext.path;

    if (usePath !== "") {
      let ref = firebase.storage().ref(usePath);
      try {
        setLoading(true);
        const dir = await ref.listAll();

        dir.items.forEach(async fileRef => {
          if (
            props.selectedFiles &&
            props.selectedFiles.find(f => f.fullPath === fileRef.fullPath)
          ) {
            removeSelectedFiles.push(fileRef);
          }
          promises.push(fileRef.delete());
        });

        dir.prefixes.forEach(folderRef => {
          deleteFolder(folderRef.fullPath, promises);
        });

        if (!dir.preFixes) {
          defer(async () => {
            await Promise.all(promises);
            setLoading(false);
            props.removeFile(removeSelectedFiles);
            if (path === menuContext.path) {
              const prevPath = path
                .split("/")
                .slice(0, -1)
                .join("/");
              handlePathChange(prevPath)();
            } else {
              setHasLoaded(false);
            }
          });
        }
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    }
  };

  const getHref = async filePath =>
    firebase
      .storage()
      .ref(filePath)
      .getDownloadURL();

  const handleAnchorEl = (context = {}) => e => {
    e.stopPropagation();
    setMenuContext(context);
    setAnchorEl(e.currentTarget);
  };

  const handlePathChange = newPath => e => {
    if (path === newPath) {
      return;
    }

    localForage.setItem("filePath", newPath, () => {
      setPath(newPath);
      setHasLoaded(false);
    });
  };

  const renderBreadcrumbs = () => {
    const tempPath = [];
    const pathArray = path.split("/");
    const crumbs = map(pathArray, path => {
      tempPath.push(path);
      const currentPath = tempPath.join("/");
      return (
        <Link
          key={path}
          className={classes.breadCrumb}
          onClick={handlePathChange(currentPath)}
        >
          {path}
        </Link>
      );
    });

    return (
      <AppBar position="sticky" color="default" elevation={1}>
        <Box p={1}>
          <Breadcrumbs separator="›" aria-label="breadcrumb">
            <span>Folder</span>
            <Link className={classes.breadCrumb} onClick={handlePathChange("")}>
              root
            </Link>
            {crumbs}
          </Breadcrumbs>
        </Box>
      </AppBar>
    );
  };

  const renderFiles = () => {
    let rowCount = 1;
    return (
      <Box height="67.5vh" maxHeight="67.5vh" overflow="auto">
        <Table stickyHeader>
          <TableHead
            path={path}
            columns={columns}
            handleAnchorEl={handleAnchorEl}
          />
          <TableBody>
            {map(files.folders, folder => (
              <FolderRow
                key={folder.name}
                rowCount={rowCount++}
                classes={classes}
                selectedFiles={props.selectedFiles}
                handleAnchorEl={handleAnchorEl}
                handlePathChange={handlePathChange}
                folder={folder}
              />
            ))}

            {reduce(
              files.files,
              (acc, file) => {
                if (file.name !== "hidden_file_1337.txt") {
                  acc.push(
                    <FileRow
                      key={file.name}
                      classes={classes}
                      rowCount={rowCount++}
                      file={file}
                      selectedFiles={props.selectedFiles}
                      addFile={props.addFile}
                      removeFile={props.removeFile}
                      setMenuContext={setMenuContext}
                      setShowDialog={setShowDialog}
                    />
                  );
                }
                return acc;
              },
              []
            )}

            {!files.folders.length &&
              !files.files.length &&
              map(new Array(20), (item, index) => (
                <RowSkeleton
                  key={`skeleton_${index}`}
                  selectedFiles={props.selectedFiles}
                />
              ))}
          </TableBody>
        </Table>
      </Box>
    );
  };

  const renderDialog = () => {
    const dialog = {
      title: "",
      buttons: [{ label: "Cancel", onClick: () => setShowDialog(false) }],
      content: () => {}
    };
    switch (showDialog) {
      case "newFolder": {
        dialog.title = "New Folder";
        dialog.content = () => (
          <form
            onSubmit={e => {
              e.preventDefault();
              createFolder();
              setShowDialog(false);
            }}
          >
            <TextField
              autoFocus
              fullWidth
              label="Folder Name"
              name="folderName"
              onChange={e => setNewFolderName(e.target.value)}
              value={newFolderName}
            />
            <Box display="none">
              <button type="submit"></button>
            </Box>
          </form>
        );
        dialog.buttons.push({
          label: "Create",
          variant: "contained",
          color: "primary",
          onClick: () => {
            createFolder();
            setShowDialog(false);
          }
        });
        break;
      }

      case "deleteFolder": {
        dialog.title = "Delete Folder";
        dialog.content = () => "Are you sure you want to delete this folder?";
        dialog.buttons.push({
          label: "Delete",
          variant: "contained",
          color: "primary",
          onClick: () => {
            deleteFolder();
            setShowDialog(false);
          }
        });
        break;
      }

      case "deleteFile": {
        dialog.title = "Delete File";
        dialog.content = () => "Are you sure you want to delete this file?";
        dialog.buttons.push({
          label: "Delete",
          variant: "contained",
          color: "primary",
          onClick: () => {
            deleteFile();
            setShowDialog(false);
          }
        });
        break;
      }

      // no default
    }

    return (
      <Dialog
        maxWidth="xs"
        title={dialog.title}
        open={showDialog}
        onClose={() => setShowDialog(false)}
        buttons={dialog.buttons}
      >
        <Box p={2}>{dialog.content()}</Box>
      </Dialog>
    );
  };

  return (
    <div className={classes.root}>
      <Box position="absolute" width="100%" zIndex={1200}>
        {(!hasLoaded || loading) && <LinearProgress />}
      </Box>
      <Box>
        <Grid container spacing={0}>
          <Grid item xs={9}>
            {renderBreadcrumbs()}
            {renderFiles()}
          </Grid>
          <Grid item xs={3} className={classes.uploadBar}>
            <FileUploader
              path={path}
              addFile={props.addFile}
              removeFile={props.removeFile}
              onUploadComplete={() => setHasLoaded(false)}
            />
          </Grid>
        </Grid>
        {renderDialog()}
      </Box>
      <Menu
        id="long-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        {menuContext.type === "folder" && (
          <MenuItem
            onClick={() => {
              setShowDialog("newFolder");
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              <CreateNewFolderIcon />
            </ListItemIcon>
            <Typography variant="inherit">
              {path === menuContext.path ? "New Folder" : "New Sub Folder"}
            </Typography>
          </MenuItem>
        )}
        {menuContext.path !== "" && menuContext.type === "folder" && (
          <MenuItem
            onClick={() => {
              setShowDialog("deleteFolder");
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              <RemoveIcon />
            </ListItemIcon>
            <Typography variant="inherit">Delete Folder</Typography>
          </MenuItem>
        )}
        {/* For Later */}
        {/* {menuContext.path !== '' && menuContext.type === 'file' && (
          <MenuItem
            onClick={() => {
              setShowDialog('deleteFile');
              setAnchorEl(null);
            }}
          >
            <ListItemIcon>
              <RemoveIcon />
            </ListItemIcon>
            <Typography variant="inherit">Delete File</Typography>
          </MenuItem>
        )} */}
      </Menu>
    </div>
  );
};

FileList.defaultProps = {
  removeFile: () => {},
  addFile: () => {}
};

export default hot(module)(FileList);
